import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {AlertCircle, CheckCircle2, FileText, Loader2, ShieldCheck} from 'lucide-react';

import {getInvoiceLinkInfo, verifyInvoiceLink} from '../api/billing/invoiceLink.api';

/**
 * 결제 예정 안내 문자의 **모바일 고지서** 화면(2026-10-02). `/invoice/view?t=...`
 *
 * ## 왜 만드나
 *
 * 예고 없이 카드 결제가 나가서 "이게 뭐냐" 문의가 많았고, 서비스명(오모플레이·단골톡)과 결제 회사명
 * (헤드리스 주식회사)이 달라 **사기로 오해**해 빌링키를 해제하거나 카드사에 민원을 넣는 일이 있었다.
 * ⇒ 결제 전에 문자로 알리고, 이 화면에서 **무엇이 얼마 청구되는지·누가 결제하는지**를 바로 보여준다.
 *
 * ## 본인 확인
 *
 * 카드 등록 링크(`CardRegisterByLink`)와 같다 — 추측 불가능한 토큰(URL) + **사업자등록번호**.
 * 서버가 5회 오입력 시 링크를 잠근다. 이 화면으로 할 수 있는 것은 **그 달 확정 고지서 읽기**뿐이다.
 *
 * 설계: `_ai-runs/2026-10-02_001_billing_payment_notice/001_design.md`
 */

/** 공개된 사업자 정보(쿠폰 페이지 하단과 같은 값). 고객이 "어느 회사인가" 를 확인하는 근거다. */
const COMPANY = {
    name: '헤드리스 주식회사',
    ceo: '남궁지환',
    bizNo: '694-86-02485',
    address: '서울특별시 마포구 마포대로 122(프론트원), 11,12층',
};

const won = (v) => `${Number(v ?? 0).toLocaleString()}원`;

const formatBizNumber = (raw) => {
    const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

/** `2026-09` → `2026년 9월`. */
const monthText = (ym) => {
    const [y, m] = String(ym ?? '').split('-');
    return y && m ? `${y}년 ${Number(m)}월` : (ym ?? '');
};

const itemTitle = (item) => (item.typeLabel ? `${item.name}(${item.typeLabel})` : item.name);

/** 확인 후 고지서 본문. 표 대신 세로 목록 — 휴대폰 폭에서 6열 표는 읽을 수 없다. */
const InvoiceBody = ({view}) => {
    const inv = view.invoice;
    const paid = !!view.paidAt;
    const isTax = view.billingMethod === 'TAX_INVOICE';

    return (
        <div>
            <div className="rounded-2xl bg-blue-600 text-white p-5">
                <div className="text-[13px] opacity-90">{monthText(view.billingMonth)} 이용료 (부가세 포함)</div>
                <div className="text-[28px] font-bold mt-1">{won(inv.total)}</div>
                <div className="mt-3 space-y-1 text-[13px]">
                    {paid ? (
                        <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4"/> 결제가 완료되었습니다</div>
                    ) : (
                        view.scheduledOn && <div>결제 예정일 · <b>{view.scheduledOn}</b></div>
                    )}
                    <div>
                        결제 방법 · {isTax ? '세금계산서 발행 후 계좌 입금' : `${view.cardLabel ?? '등록하신 카드'} 자동 결제`}
                    </div>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 leading-relaxed">
                <b>{view.brand}</b> 서비스 이용료는 운영사인 <b>{COMPANY.name}</b> 명의로 결제됩니다.
                카드 승인 내역에는 서비스명 대신 회사명이 표시될 수 있습니다.
            </div>

            <div className="mt-5 space-y-4">
                {inv.groups?.map((g) => (
                    <div key={g.category} className="rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between bg-gray-50 rounded-t-xl px-4 py-2.5 border-b border-gray-200">
                            <span className="text-[14px] font-semibold text-gray-800">{g.categoryLabel}</span>
                            <span className="text-[14px] font-semibold text-gray-800">{won(g.total)}</span>
                        </div>
                        <ul className="divide-y divide-gray-100">
                            {g.items.map((item, idx) => (
                                <li key={idx} className="px-4 py-2.5">
                                    <div className="flex items-start justify-between gap-3">
                                        <span className="text-[13px] text-gray-800 leading-snug">
                                            {itemTitle(item)}
                                            {item.vatExempt && <span className="ml-1 text-[11px] text-gray-400">(면세)</span>}
                                        </span>
                                        <span className="text-[13px] text-gray-900 whitespace-nowrap">{won(item.supplyAmountNet)}</span>
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-gray-400">
                                        {Number(item.unitPriceNet ?? 0).toLocaleString()}원 × {Number(item.quantity ?? 0).toLocaleString()}
                                        {item.coverage && ` · ${item.coverage}`}
                                    </div>
                                    {item.description && <div className="mt-0.5 text-[11px] text-gray-400">{item.description}</div>}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-[14px] space-y-1.5">
                <div className="flex justify-between text-gray-600"><span>공급가액</span><span>{won(inv.supplyTotal)}</span></div>
                <div className="flex justify-between text-gray-600"><span>부가세</span><span>{won(inv.vat)}</span></div>
                <div className="flex justify-between font-bold text-gray-900 pt-1.5 border-t border-gray-200">
                    <span>합계</span><span>{won(inv.total)}</span>
                </div>
            </div>
            <p className="mt-2 text-[11px] text-gray-400 leading-relaxed">
                단가·공급가액은 부가세 별도 금액이며, 부가세는 과세 공급가액 합계에 한 번만 적용됩니다.
            </p>

            {inv.customerRemark && (
                <div className="mt-4 rounded-xl border border-gray-200">
                    <div className="bg-gray-50 rounded-t-xl px-4 py-2 text-[13px] font-semibold text-gray-800 border-b border-gray-200">안내 사항</div>
                    <div className="px-4 py-3 text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">{inv.customerRemark}</div>
                </div>
            )}
        </div>
    );
};

export default function InvoiceByLink() {
    const [query] = useSearchParams();
    const token = query.get('t');

    const [isLoading, setIsLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [linkError, setLinkError] = useState(null);

    const [bizRegNumber, setBizRegNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState(null);

    useEffect(() => {
        if (!token) {
            setLinkError('링크가 올바르지 않습니다. 받으신 문자의 링크를 다시 확인해주세요.');
            setIsLoading(false);
            return;
        }
        getInvoiceLinkInfo(token)
            .then((res) => {
                const data = res?.data;
                if (!data?.valid) {
                    setLinkError(data?.message ?? '링크가 올바르지 않습니다.');
                    return;
                }
                setInfo(data);
            })
            .catch(() => setLinkError('링크를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.'))
            .finally(() => setIsLoading(false));
    }, [token]);

    const submit = () => {
        const digits = bizRegNumber.replace(/\D/g, '');
        if (digits.length !== 10) {
            setError('사업자등록번호 10자리를 입력해주세요.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        verifyInvoiceLink(token, digits)
            .then((res) => {
                const data = res?.data;
                if (!data?.verified) {
                    const remain = data?.remainingAttempts;
                    setError(remain != null
                        ? `${data?.message ?? '확인하지 못했습니다.'} (남은 입력 ${remain}회)`
                        : (data?.message ?? '확인하지 못했습니다.'));
                    return;
                }
                setView(data.invoice);
            })
            .catch(() => setError('확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'))
            .finally(() => setIsSubmitting(false));
    };

    const brand = view?.brand ?? info?.brand ?? '오모플레이';

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-8">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-3xl shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-5 h-5 text-blue-600"/>
                        <h1 className="text-[20px] font-bold text-gray-900">{brand} 이용료 고지서</h1>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-5">
                        {info?.billingMonth ? `${monthText(info.billingMonth)} 이용분` : '서비스 이용료 안내'}
                    </p>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 gap-2 text-[14px]">
                            <Loader2 className="w-5 h-5 animate-spin"/> 확인 중입니다.
                        </div>
                    ) : linkError ? (
                        <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5"/>
                            <p className="text-[13px] text-red-800 leading-relaxed">{linkError}</p>
                        </div>
                    ) : view ? (
                        view.confirmed && view.invoice ? (
                            <InvoiceBody view={view}/>
                        ) : (
                            <div className="rounded-xl bg-gray-50 border border-gray-200 p-5 text-center">
                                <div className="text-[15px] font-semibold text-gray-800">고지서를 다시 확인하고 있습니다.</div>
                                <div className="mt-2 text-[13px] text-gray-500 leading-relaxed">
                                    금액을 확정하는 대로 다시 안내드리겠습니다.<br/>확정 전에는 결제가 진행되지 않습니다.
                                </div>
                            </div>
                        )
                    ) : (
                        <>
                            {info?.storeName && (
                                <div className="mb-5 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                                    <span className="text-[12px] text-gray-500">업체명</span>
                                    <div className="text-[15px] font-bold text-gray-900">{info.storeName}</div>
                                </div>
                            )}
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">사업자등록번호</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={bizRegNumber}
                                onChange={(e) => setBizRegNumber(formatBizNumber(e.target.value))}
                                onKeyDown={(e) => e.key === 'Enter' && !isSubmitting && submit()}
                                placeholder="123-45-67890"
                                className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-xl text-[16px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-300"
                            />
                            <p className="mt-2 text-[12px] text-gray-400 leading-relaxed">
                                고지서는 사업자등록번호를 확인한 뒤에 보여드립니다.
                            </p>
                            {error && (
                                <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5"/>
                                    <p className="text-[13px] text-red-800 leading-relaxed">{error}</p>
                                </div>
                            )}
                            <button
                                onClick={submit}
                                disabled={isSubmitting}
                                className="mt-5 w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                            >
                                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>}
                                고지서 확인하기
                            </button>
                        </>
                    )}
                </div>

                {/* 회사 정보 — "모르는 회사에서 결제했다" 는 오해를 막는 근거라 화면마다 항상 보인다. */}
                <div className="mt-5 px-2 text-[11px] text-gray-400 leading-relaxed space-y-0.5">
                    <p>{brand} 서비스는 {COMPANY.name}가 운영합니다.</p>
                    <p>{COMPANY.name} · 대표 {COMPANY.ceo} · 사업자등록번호 {COMPANY.bizNo}</p>
                    <p>{COMPANY.address}</p>
                </div>
            </div>
        </div>
    );
}
